import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarBlank, CaretDown, CaretLeft, CaretRight, X } from "@phosphor-icons/react";
import api from "../lib/api";

/**
 * DateFilterPicker — compact button + bottom-sheet calendar for the
 * customer-facing date filter. Replaces the old segmented Today /
 * Tomorrow / Week / Next Week pills with a single tap target that
 * opens a full calendar grid.
 *
 * Props:
 *   value: current filter ("today" | "tomorrow" | "weekend" | "week" |
 *     "next_week" | "followed" | "date:YYYY-MM-DD")
 *   onChange(filter): callback when the customer applies a new selection
 *
 * Behaviour: tapping a calendar cell **immediately** commits the
 * selection and closes the sheet — no Apply button. The previous
 * pending-state + sticky CTA pattern read as broken to users (looked
 * like the tap did nothing). The only reason to dismiss without
 * picking is the close ✕ in the corner.
 *
 * The label format auto-adapts to the chosen value (e.g. "Today, May 6",
 * "This week", "Sat, May 10").
 */

const WEEKDAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function isoLocal(d) {
  // Local YYYY-MM-DD — matches how the backend serialises calendar dates.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function sameDay(a, b) {
  return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function fmtShort(d) {
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function labelForFilter(filter) {
  if (!filter) return "Today";
  if (filter === "today") {
    return `Today, ${new Date().toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  }
  if (filter === "tomorrow") {
    const t = new Date();
    t.setDate(t.getDate() + 1);
    return `Tomorrow, ${t.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  }
  if (filter === "weekend") return "This Weekend";
  if (filter === "week") return "This Week";
  if (filter === "next_week") return "Next Week";
  if (filter === "followed") return "⭐ Your Trucks";
  if (filter.startsWith("date:")) {
    const [y, m, d] = filter.slice(5).split("-").map(Number);
    return fmtShort(new Date(y, m - 1, d));
  }
  return "Today";
}

export default function DateFilterPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  // Calendar carousel state — start on the month that contains today.
  const [monthAnchor, setMonthAnchor] = useState(() => startOfMonth(new Date()));
  // Truck-count map keyed by YYYY-MM-DD
  const [counts, setCounts] = useState({});

  // Jump the calendar to the month of the active filter when the sheet
  // opens. Otherwise default to the month containing today.
  useEffect(() => {
    if (!open) return;
    if (value && value.startsWith && value.startsWith("date:")) {
      const [y, m, d] = value.slice(5).split("-").map(Number);
      setMonthAnchor(startOfMonth(new Date(y, m - 1, d)));
    } else {
      setMonthAnchor(startOfMonth(new Date()));
    }
  }, [open, value]);

  // Lazy-load truck counts the first time the sheet opens.
  useEffect(() => {
    if (!open) return;
    let mounted = true;
    api
      .get("/trucks/calendar", { params: { days: 365 } })
      .then(({ data }) => {
        if (!mounted) return;
        const map = {};
        (data?.days || []).forEach((d) => { map[d.date] = d.count; });
        setCounts(map);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, [open]);

  // Build the 6-row x 7-col grid for the visible month (Sunday-first).
  const grid = useMemo(() => {
    const first = startOfMonth(monthAnchor);
    const startWeekday = first.getDay(); // 0=Sun
    const totalCells = 42; // 6 weeks
    const cells = [];
    for (let i = 0; i < totalCells; i++) {
      const dayNum = i - startWeekday + 1;
      const date = new Date(first.getFullYear(), first.getMonth(), dayNum);
      cells.push(date);
    }
    return cells;
  }, [monthAnchor]);

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  // Read the currently-active date (if filter is `date:...` or the
  // implicit "today") so we can render the orange-filled cell on the
  // correct day. We treat `filter==="today"` as today's date.
  const activeDate = useMemo(() => {
    if (!value) return null;
    if (value === "today") return today;
    if (value.startsWith && value.startsWith("date:")) {
      const [y, m, d] = value.slice(5).split("-").map(Number);
      return new Date(y, m - 1, d);
    }
    return null;
  }, [value, today]);

  const onPickDate = (d) => {
    // Tapping today's cell → use the cleaner "today" filter (inherits
    // overlap semantics so live-since-yesterday trucks still appear).
    if (sameDay(d, today)) {
      onChange("today");
    } else {
      onChange(`date:${isoLocal(d)}`);
    }
    setOpen(false);
  };

  const monthLabel = monthAnchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <>
      {/* Compact button — collapses the old 5-pill segmented control */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="pointer-events-auto inline-flex items-center gap-1.5 h-9 sm:h-10 px-3.5 rounded-full border border-border bg-card/95 backdrop-blur-xl shadow-sm text-sm font-medium hover:bg-muted transition"
        data-testid="date-filter-button"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <CalendarBlank weight="duotone" className="h-4 w-4 text-primary" />
        <span className="truncate max-w-[160px] sm:max-w-[220px]">{labelForFilter(value)}</span>
        <CaretDown weight="bold" className="h-3 w-3 text-muted-foreground" />
      </button>

      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              // z-[1000] / z-[1001] — Leaflet creates its own stacking
              // panes up to ~800 (controls). Anything ≤ 800 gets pointer
              // events stolen by Leaflet. Plus `pointer-events-auto` is
              // CRITICAL: this picker is rendered inside the floating
              // header which has `pointer-events-none`, and pointer-
              // events inherits down the DOM tree even across fixed
              // descendants. Without this override, the sheet looks
              // open but every click passes through to Leaflet — the
              // exact bug users reported in production.
              className="pointer-events-auto fixed inset-0 z-[1000] bg-black/60 backdrop-blur-sm"
              data-testid="date-filter-backdrop"
            />
            {/* Bottom sheet */}
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label="Pick a date"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 320 }}
              className="pointer-events-auto fixed inset-x-0 bottom-0 z-[1001] bg-card border-t border-border rounded-t-3xl shadow-2xl max-h-[92dvh] overflow-y-auto"
              data-testid="date-filter-sheet"
            >
              {/* Drag handle */}
              <div className="flex justify-center pt-2.5 pb-1">
                <div className="h-1.5 w-10 rounded-full bg-muted" />
              </div>

              <div className="px-5 pb-6 pt-2 max-w-xl mx-auto">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="font-display font-bold text-xl tracking-tight">Pick a date</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Tap any day with an orange dot to see who's out
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="h-9 w-9 grid place-items-center rounded-full hover:bg-muted text-muted-foreground"
                    aria-label="Close"
                    data-testid="date-filter-close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {/* Month nav */}
                <div className="flex items-center justify-between mb-3">
                  <button
                    type="button"
                    onClick={() => {
                      const prev = new Date(monthAnchor);
                      prev.setMonth(prev.getMonth() - 1);
                      setMonthAnchor(startOfMonth(prev));
                    }}
                    className="h-9 w-9 grid place-items-center rounded-full hover:bg-muted text-muted-foreground"
                    aria-label="Previous month"
                    data-testid="date-month-prev"
                  >
                    <CaretLeft weight="bold" className="h-4 w-4" />
                  </button>
                  <div className="font-semibold text-base" data-testid="date-month-label">{monthLabel}</div>
                  <button
                    type="button"
                    onClick={() => {
                      const next = new Date(monthAnchor);
                      next.setMonth(next.getMonth() + 1);
                      setMonthAnchor(startOfMonth(next));
                    }}
                    className="h-9 w-9 grid place-items-center rounded-full hover:bg-muted text-muted-foreground"
                    aria-label="Next month"
                    data-testid="date-month-next"
                  >
                    <CaretRight weight="bold" className="h-4 w-4" />
                  </button>
                </div>

                {/* Weekday header */}
                <div className="grid grid-cols-7 mb-1 text-[11px] text-muted-foreground font-semibold uppercase tracking-wider">
                  {WEEKDAY_LABELS.map((w, i) => (
                    <div key={i} className="text-center py-1">{w}</div>
                  ))}
                </div>

                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-1" data-testid="date-calendar-grid">
                  {grid.map((d, i) => {
                    const inMonth = d.getMonth() === monthAnchor.getMonth();
                    const isToday = sameDay(d, today);
                    const isActive = sameDay(d, activeDate);
                    const isPast = d < today && !isToday;
                    const key = isoLocal(d);
                    const count = counts[key] || 0;
                    const hasTrucks = count > 0;
                    return (
                      <button
                        key={i}
                        type="button"
                        disabled={isPast}
                        onClick={() => onPickDate(d)}
                        className={
                          "relative aspect-square rounded-xl flex flex-col items-center justify-center text-sm font-medium transition select-none " +
                          (isActive
                            ? "bg-primary text-primary-foreground shadow-sm"
                            : !inMonth
                            ? "text-muted-foreground/40 hover:bg-muted"
                            : isPast
                            ? "text-muted-foreground/40 cursor-not-allowed"
                            : isToday
                            ? "bg-primary/10 text-primary border border-primary/40 hover:bg-primary/15"
                            : hasTrucks
                            ? "hover:bg-muted text-foreground"
                            : "hover:bg-muted text-muted-foreground")
                        }
                        data-testid={`date-cell-${key}`}
                        aria-label={`${d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}${hasTrucks ? `, ${count} truck${count === 1 ? "" : "s"}` : ""}`}
                      >
                        <span className="leading-none">{d.getDate()}</span>
                        {hasTrucks && (
                          <span
                            className={
                              "mt-0.5 text-[10px] font-bold leading-none " +
                              (isActive ? "text-primary-foreground" : "text-primary")
                            }
                          >
                            {count}
                          </span>
                        )}
                        {hasTrucks && !isActive && (
                          <span
                            className="absolute bottom-1 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-primary"
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
